import { useQuery } from "@tanstack/react-query"
import { AlertCircle, Plus, Users } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { type Cliente, FormularioCliente } from "@/componentes/catalogos/FormularioCliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Aparece } from "@/componentes/ui/Aparece"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Catálogo de clientes.
 *
 * ⚠ **Es el primer eslabón de la cadena operativa.** Sin poder dar de alta un
 * cliente no hay a quién cotizar, y sin cotización no nace un expediente: hasta que
 * existió esta pantalla, todo lo que había en el sistema salía del `seed`.
 *
 * ⚠ **Nadie se borra: se desactiva.** Un cliente tiene expedientes y cobros
 * emitidos, y borrarlo destruiría el rastro de lo que se le facturó. La búsqueda y
 * el filtro de activos son la forma de sacarlo de en medio.
 *
 * La búsqueda va al SERVIDOR —`?buscar=`—, como la paginación. Filtrar en el
 * navegador funciona con los datos de prueba y muere el primer día de producción.
 */

export function Clientes() {
  const usuario = usarSesion((e) => e.usuario)
  const [buscar, setBuscar] = useState("")
  const [soloActivos, setSoloActivos] = useState(true)
  const [editando, setEditando] = useState<Cliente | null>(null)
  const [creando, setCreando] = useState(false)

  const parametros = new URLSearchParams({ limit: "100" })
  if (buscar.trim() !== "") parametros.set("buscar", buscar.trim())
  if (soloActivos) parametros.set("activo", "true")

  const consulta = useQuery({
    queryKey: ["clientes", { buscar: buscar.trim(), soloActivos }],
    queryFn: () => api.get<Cliente[]>(`/api/v1/clientes?${parametros}`),
  })

  const puedeAdministrar = tienePermiso(usuario, "cliente:administrar")
  const borde = { borderColor: "var(--border)" }
  const filas = consulta.data ?? []
  const vista = useRef<HTMLDivElement | null>(null)
  const subtitulo = [
    soloActivos ? "Sólo activos" : "Activos e inactivos",
    buscar.trim() !== "" ? `Búsqueda: «${buscar.trim()}»` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        {/* Vuelve a ser una entrada del menú —«Comercial» es una SECCIÓN, no una
            pantalla—, así que vuelve a poner su título. */}
        <div>
          <h1 className="text-xl font-semibold">Clientes</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {consulta.data ? `${filas.length} en la página` : " "}
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-2">
          <Exportar titulo="Clientes" subtitulo={subtitulo} contenido={vista} />
          {puedeAdministrar && (
            <button
              type="button"
              onClick={() => {
                setEditando(null)
                setCreando((abierto) => !abierto)
              }}
              aria-expanded={creando}
              className="flex h-9 items-center gap-2 rounded-[var(--radius-control)] px-3 text-sm font-medium"
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
            >
              <Plus size={16} aria-hidden="true" />
              Nuevo cliente
            </button>
          )}
        </div>
      </div>

      <Aparece visible={creando} className="mb-6">
        <FormularioCliente cliente={null} onCerrar={() => setCreando(false)} />
      </Aparece>

      <Aparece visible={editando !== null} className="mb-6">
        {editando !== null && (
          <FormularioCliente cliente={editando} onCerrar={() => setEditando(null)} />
        )}
      </Aparece>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <label className="text-sm" htmlFor="buscar-cliente">
          Buscar
          <input
            id="buscar-cliente"
            className="ml-2 h-9 rounded-[var(--radius-control)] border px-2 text-sm"
            style={borde}
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Nombre, código o RIF"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={soloActivos}
            onChange={(e) => setSoloActivos(e.target.checked)}
          />
          Sólo activos
        </label>
      </div>

      {consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando clientes…
        </p>
      )}

      {consulta.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 superficie p-4"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={20} aria-hidden="true" />
          <p>
            {consulta.error instanceof ErrorApi
              ? consulta.error.message
              : "No se pudieron cargar los clientes"}
          </p>
        </div>
      )}

      {consulta.data && filas.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <Users size={24} aria-hidden="true" />
          <p className="text-sm">
            {buscar.trim() !== "" ? "Ningún cliente coincide" : "Todavía no hay clientes"}
          </p>
        </div>
      )}

      {consulta.data && filas.length > 0 && (
        <div ref={vista}>
          <Tabla etiqueta="Clientes registrados">
            <Cabecera>
              <Th>Código</Th>
              <Th>Razón social</Th>
              <Th>RIF</Th>
              <Th>Contacto</Th>
              <Th>Estado</Th>
              {puedeAdministrar && <Th />}
            </Cabecera>
            <Cuerpo>
              {filas.map((c) => (
                <Fila key={c.id} data-testid={`cliente-${c.id}`}>
                  <Td numero className="font-medium">
                    {c.codigo}
                  </Td>
                  <Td>{c.razon_social}</Td>
                  <Td numero>{c.rif}</Td>
                  <Td style={{ color: "var(--fg-muted)" }}>{c.email ?? c.telefono ?? "—"}</Td>
                  <Td>
                    <Insignia tono={c.activo ? "exito" : "neutro"}>
                      {c.activo ? "Activo" : "Inactivo"}
                    </Insignia>
                  </Td>
                  {puedeAdministrar && (
                    <Td derecha>
                      <button
                        type="button"
                        data-testid={`editar-cliente-${c.id}`}
                        onClick={() => {
                          setCreando(false)
                          setEditando(c)
                        }}
                        className="rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                        style={borde}
                      >
                        Editar
                      </button>
                    </Td>
                  )}
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      )}
    </div>
  )
}
